import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, Controller } from "react-hook-form";
import { useApolloClient } from "@apollo/client";
import Select from "react-select/async";
import SelectStatic from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { debounce, pick, get, isEqual, sortBy } from "lodash";
import { useSearchParams } from "react-router-dom";
import Slider from "rc-slider";
import rison from "rison";
import "rc-slider/assets/index.css";

import {
  Root,
  Container,
  ActionsList,
  ActionsItem,
  BrandAction,
  Checkbox,
  CategoryAction,
  ExpandButtonContainer,
  ClearButton,
  ExpandButton,
  AdvancedContainer,
  Fields,
  FieldsContainer,
  RangeContainer,
} from "./FiltersDesktop.style";
import {
  SearchField,
  SearchIcon,
  RangeSliderContainer,
  RangeInputContainer,
  RangeInput,
  SearchInput,
} from "../FiltersSharedComponents/Filters.style";

import {
  SearchClearIndicator,
  SearchOption,
  searchSelectStyles,
} from "../FiltersSharedComponents/searchSelectCustom";
import {
  DropdownIndicator,
  MultiValueRemove,
  selectStyles,
} from "../FiltersSharedComponents/selectCustom";
import { brands } from "../../config/brands";
import { Typography } from "../../ui/Typography/Typography";
// import { parseSearchParams } from "../../utils/location";

const DistributorInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 8px;
  font-size: 14px;
  letter-spacing: 0.14px;
  border: solid 1px #c2c8d0;
  border-radius: 5px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #7f8696;
  }
`;

const DateRangeInput = styled.div`
  width: 100%;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container input {
    width: 100%;
    height: 40px;
    padding: 0 8px;
    font-size: 14px;
    letter-spacing: 0.14px;
    border: solid 1px #c2c8d0;
    border-radius: 5px;
    outline: none;
    box-sizing: border-box;
  }
`;

export const FiltersDesktop = ({
  categoriesConfig = [],
  showPoints = true,
  pointsConfig = { min: 0, max: 100 },
  tagsConfig,
  advancedFilters = true,
  searchPlaceholder = "Keyword search",
}) => {
  const intl = useIntl();
  const client = useApolloClient();
  const firstRender = useRef(true);
  const onChangeEnabled = useRef(true);
  const searchParamsRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  searchParamsRef.current = searchParams;
  const [isExpandedFilters, setIsExpandedFilters] = useState(false);

  const defaultValues = {
    brands: [],
    categories: [],
    search: "",
    tags: [],
    points: [pointsConfig.min, pointsConfig.max],
    distributor: "",
    province: "",
    subCategory: [],
    dateRange: null,
  };

  const {
    register,
    control,
    watch,
    reset,
    resetField,
    handleSubmit,
    setValue,
    getValues,
    formState: { isDirty },
  } = useForm({
    defaultValues,
  });

  useEffect(() => {
    const filters = searchParams.get("filters");
    const decoded = filters != null ? rison.decode(filters) : {};
    const formValue = {
      ...defaultValues,
      ...pick(decoded, [
        "brands",
        "search",
        "categories",
        "tags",
        "points",
        "province",
        "subCategory",
        "dateRange",
      ]),
    };
    if (decoded.distributor != null) {
      formValue.distributor =
        typeof decoded.distributor === "object" && decoded.distributor?.$regex != null
          ? decoded.distributor.$regex
          : decoded.distributor;
    }
    if (decoded["data.distributor"] != null) {
      formValue.distributor =
        typeof decoded["data.distributor"] === "object" && decoded["data.distributor"]?.$regex != null
          ? decoded["data.distributor"].$regex
          : decoded["data.distributor"];
    }
    const dataDist = decoded.data?.distributor;
    if (dataDist != null) {
      formValue.distributor =
        typeof dataDist === "object" && dataDist?.$regex != null
          ? dataDist.$regex
          : dataDist;
    }

    //deal with bullshit where single values are not arrays
    const arrayVals = ["brands", "categories", "tags", "subCategory"];
    arrayVals.forEach((key) => {
      if (!Array.isArray(formValue[key])) {
        formValue[key] = [formValue[key]];
      }
    });

    //disable onchange for browner back/forward buttons
    if (!firstRender.current) {
      onChangeEnabled.current = false;
    }
    Object.keys(formValue).forEach((key) => {
      if (!isEqual(watchForm[key], formValue[key])) {
        setValue(key, formValue[key], { shouldDirty: true });
      }
    });
    //re-enable onchange
    if (!firstRender.current) {
      onChangeEnabled.current = true;
    }

    if (firstRender.current) {
      firstRender.current = false;
    }
  }, [searchParams]);

  const applyFiltersToUrl = useCallback(
    (data) => {
      const params = new URLSearchParams(searchParamsRef.current ?? undefined);
      let scrubbedData = Object.fromEntries(
        Object.entries(data).filter(
          ([_, v]) => v != null && v !== "" && v?.length !== 0
        )
      );

      if (scrubbedData.points && scrubbedData.points[0] === "") {
        scrubbedData.points[0] = null;
      }
      if (scrubbedData.points && scrubbedData.points[1] === "") {
        scrubbedData.points[1] = null;
      }
      if (
        scrubbedData.points &&
        ((parseInt(scrubbedData.points[0], 10) === pointsConfig.min &&
          parseInt(scrubbedData.points[1], 10) === pointsConfig.max) ||
          (scrubbedData.points[0] == null && scrubbedData.points[1] == null))
      ) {
        delete scrubbedData.points;
      }

      if (
        scrubbedData.dateRange &&
        (!scrubbedData.dateRange.start || !scrubbedData.dateRange.end)
      ) {
        delete scrubbedData.dateRange;
      }

      if (
        scrubbedData.distributor &&
        typeof scrubbedData.distributor === "string"
      ) {
        const trimmed = scrubbedData.distributor.trim();
        if (trimmed) {
          scrubbedData["data.distributor"] = {
            $regex: trimmed,
            $options: "i",
          };
        }
        delete scrubbedData.distributor;
      }

      if (Object.keys(scrubbedData).length === 0) {
        params.delete("filters");
      } else {
        params.set("filters", rison.encode(scrubbedData));
      }
      setSearchParams(params, { preventScrollReset: true });
    },
    [setSearchParams, pointsConfig.min, pointsConfig.max]
  );

  const getValuesRef = useRef(getValues);
  const applyFiltersToUrlRef = useRef(applyFiltersToUrl);

  useEffect(() => {
    getValuesRef.current = getValues;
  }, [getValues]);

  useEffect(() => {
    applyFiltersToUrlRef.current = applyFiltersToUrl;
  }, [applyFiltersToUrl]);

  const debouncedSetDistributor = useMemo(
    () =>
      debounce(
        (value) => {
          const data = { ...getValuesRef.current(), distributor: value };
          applyFiltersToUrlRef.current(data);
        },
        300,
        { leading: false, trailing: true }
      ),
    []
  );

  useEffect(() => {
    return () => debouncedSetDistributor.cancel();
  }, [debouncedSetDistributor]);

  const debouncedOnChange = useMemo(
    () =>
      debounce(
        (data) => {
          applyFiltersToUrl(data);
        },
        300,
        { leading: false, trailing: true, maxWait: 1000 }
      ),
    [applyFiltersToUrl]
  );

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel(); // Stop the invocation of the debounced function
    };
  }, []);

  const watchCategories = watch("categories");
  // console.info("watchCategories: ", watchCategories);
  // console.info("isDirty: ", isDirty);

  watch((data, opts) => {
    if (!onChangeEnabled.current) {
      return;
    }
    // Distributor input is handled by its own debounce.
    if (opts?.name === "distributor") {
      return;
    }
    debouncedOnChange(data);
  });

  const watchForm = watch();

  return (
    <Root>
      <Container className="--bg">
        <ActionsList>
          {sortBy(brands, ["label"]).map((brand) => (
            <ActionsItem key={brand.slug}>
              <Checkbox
                {...register("brands")}
                type="checkbox"
                name="brands"
                value={brand.id}
                id={brand.id}
              />
              <BrandAction htmlFor={brand.id}>{brand.icon}</BrandAction>
            </ActionsItem>
          ))}
        </ActionsList>

        {categoriesConfig.length > 0 && (
          <ActionsList>
            <CategoryAction
              as="button"
              type="button"
              $isActive={watchCategories?.length === 0}
              onClick={() => resetField("categories")}
            >
              <FormattedMessage defaultMessage="all" />
            </CategoryAction>
            {categoriesConfig.map((category) => (
              <ActionsItem key={category.value}>
                <Checkbox
                  {...register("categories")}
                  type="checkbox"
                  name="categories"
                  value={category.value}
                  id={category.value}
                />
                <CategoryAction
                  htmlFor={category.value}
                  $isActive={(watchCategories || []).includes(category.value)}
                >
                  {category.label}
                </CategoryAction>
              </ActionsItem>
            ))}
          </ActionsList>
        )}

        <ExpandButtonContainer>
          {isDirty && (
            <ClearButton type="button" onClick={() => reset()}>
              <FormattedMessage defaultMessage="Clear All" />
            </ClearButton>
          )}
          {advancedFilters && (
            <ExpandButton
              type="button"
              onClick={() => setIsExpandedFilters(!isExpandedFilters)}
            >
              <span>
                {isExpandedFilters ? (
                  <FormattedMessage defaultMessage="Hide Advanced Filters" />
                ) : (
                  <FormattedMessage defaultMessage="Show Advanced Filters" />
                )}
                &nbsp;&nbsp;
              </span>
              {isExpandedFilters ? (
                <svg
                  width={14}
                  viewBox="0 0 14 14"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="currentColor"
                    d="M 2.75 6.306 L 11.25 6.306 L 11.25 7.695 L 2.75 7.695 Z"
                  ></path>
                </svg>
              ) : (
                <svg
                  width={14}
                  viewBox="0 0 14 14"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="currentColor"
                    d="M6.306 2.75h1.389v3.243l.313.313h3.242v1.389H7.978l-.283.283v3.272H6.306V7.978l-.283-.283H2.75v-1.39h3.243l.313-.313V2.75Z"
                  />
                </svg>
              )}
            </ExpandButton>
          )}
        </ExpandButtonContainer>
      </Container>

      {isExpandedFilters && (
        <Container>
          <AdvancedContainer>
            <Fields style={{ maxWidth: showPoints ? 685 : 460 }}>
              <SearchField>
                <SearchIcon />
                <SearchInput
                  type="text"
                  placeholder={searchPlaceholder}
                  {...register("search")}
                />
                {/* <Controller
                  name="search"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      placeholder="Search a reward"
                      type="text"
                    />
                  )}
                /> */}
              </SearchField>
              <FieldsContainer className={showPoints && "--twoColumns"}>
                <div>
                  <Typography
                    as="label"
                    type="text-small"
                    style={{ marginBottom: 8 }}
                  >
                    <FormattedMessage defaultMessage="Tags" />
                  </Typography>
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field }) => {
                      return (
                        <Select
                          {...field}
                          placeholder={intl.formatMessage({
                            defaultMessage: "Select tags",
                          })}
                          isClearable={false}
                          components={{
                            IndicatorSeparator: () => null,
                            DropdownIndicator,
                            MultiValueRemove,
                          }}
                          isMulti
                          defaultOptions={true}
                          loadOptions={async (inputValue) => {
                            return client
                              .query({
                                query: tagsConfig.gql,
                                variables: {
                                  startsWith: inputValue,
                                },
                                fetchPolicy: "network-only",
                              })
                              .then((response) => {
                                const tags = get(response, tagsConfig.dataPath);
                                return tags.map((tag) => ({
                                  label: tag,
                                  value: tag,
                                }));
                              });
                          }}
                          value={field.value.map((itm) => ({
                            label: itm,
                            value: itm,
                          }))}
                          //remove label and only pass value
                          onChange={(e, { name }) => {
                            const val = Array.isArray(e)
                              ? e.flatMap((itm) => itm.value)
                              : e?.value;
                            return field.onChange(val);
                          }}
                          styles={selectStyles}
                        />
                      );
                    }}
                  />
                </div>
                {showPoints && (
                  <div>
                    <Typography
                      as="label"
                      type="text-small"
                      style={{ marginBottom: 8 }}
                    >
                      Points
                    </Typography>
                    <RangeContainer>
                      {/* <RangeSliderContainer>
                        <Controller
                          name="points"
                          control={control}
                          render={({ field }) => (
                            <Slider
                              range
                              {...field}
                              min={pointsConfig.min}
                              max={pointsConfig.max}
                              styles={{
                                handle: {
                                  height: 14,
                                  width: 14,
                                  backgroundColor: "#e1000f",
                                  opacity: 1,
                                  border: 0,
                                  boxShadow: "none",
                                },
                                track: {
                                  backgroundColor: "#ebe8e8",
                                },
                              }}
                            />
                          )}
                        />
                      </RangeSliderContainer> */}
                      <RangeInputContainer>
                        <Controller
                          name="points"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <>
                              <span style={{ fontSize: "12px" }}>
                                <FormattedMessage defaultMessage="Min:" />
                              </span>
                              <RangeInput
                                placeholder="0"
                                value={value[0] ?? ""}
                                onChange={(e) => {
                                  const min = e.target.value;
                                  const max = value[1];
                                  onChange([min, max]);
                                }}
                              />
                            </>
                          )}
                        />
                        <Controller
                          name="points"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <>
                              <span style={{ fontSize: "12px" }}>
                                <FormattedMessage defaultMessage="Max:" />
                              </span>
                              <RangeInput
                                value={value[1] ?? ""}
                                placeholder="100000"
                                onChange={(e) => {
                                  const min = value[0];
                                  const max = e.target.value;
                                  onChange([min, max]);
                                }}
                              />
                            </>
                          )}
                        />
                      </RangeInputContainer>
                    </RangeContainer>
                  </div>
                )}
                <div>
                  <Typography
                    as="label"
                    type="text-small"
                    style={{ marginBottom: 8 }}
                  >
                    <FormattedMessage defaultMessage="Distributor" />
                  </Typography>
                  <Controller
                    name="distributor"
                    control={control}
                    render={({ field }) => {
                      const { onChange, value, ...rest } = field;
                      return (
                        <DistributorInput
                          {...rest}
                          value={value || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            onChange(v);
                            debouncedSetDistributor(v);
                          }}
                          type="text"
                          placeholder={intl.formatMessage({
                            defaultMessage: "Enter distributor name",
                          })}
                        />
                      );
                    }}
                  />
                </div>
                <div>
                  <Typography
                    as="label"
                    type="text-small"
                    style={{ marginBottom: 8 }}
                  >
                    <FormattedMessage defaultMessage="Province" />
                  </Typography>
                  <Controller
                    name="province"
                    control={control}
                    render={({ field }) => {
                      const provinceOptions = [
                        { value: 'AB', label: 'Alberta' },
                        { value: 'BC', label: 'British Columbia' },
                        { value: 'MB', label: 'Manitoba' },
                        { value: 'NB', label: 'New Brunswick' },
                        { value: 'NL', label: 'Newfoundland and Labrador' },
                        { value: 'NS', label: 'Nova Scotia' },
                        { value: 'NT', label: 'Northwest Territories' },
                        { value: 'NU', label: 'Nunavut' },
                        { value: 'ON', label: 'Ontario' },
                        { value: 'PE', label: 'Prince Edward Island' },
                        { value: 'QC', label: 'Quebec' },
                        { value: 'SK', label: 'Saskatchewan' },
                        { value: 'YT', label: 'Yukon' },
                      ];
                      const selectedOption = field.value 
                        ? provinceOptions.find(opt => opt.value === field.value)
                        : null;
                      return (
                        <SelectStatic
                          placeholder={intl.formatMessage({
                            defaultMessage: "Select province",
                          })}
                          isClearable={true}
                          components={{
                            IndicatorSeparator: () => null,
                            DropdownIndicator,
                          }}
                          options={provinceOptions}
                          value={selectedOption}
                          onChange={(e) => field.onChange(e?.value || "")}
                          styles={selectStyles}
                        />
                      );
                    }}
                  />
                </div>
                <div>
                  <Typography
                    as="label"
                    type="text-small"
                    style={{ marginBottom: 8 }}
                  >
                    <FormattedMessage defaultMessage="Sub-category" />
                  </Typography>
                  <Controller
                    name="subCategory"
                    control={control}
                    render={({ field }) => (
                      <SelectStatic
                        {...field}
                        placeholder={intl.formatMessage({
                          defaultMessage: "Select categories",
                        })}
                        isClearable={false}
                        components={{
                          IndicatorSeparator: () => null,
                          DropdownIndicator,
                          MultiValueRemove,
                        }}
                        isMulti
                        options={[
                          { value: 'Cut', label: 'Cut' },
                          { value: 'Color', label: 'Color' },
                          { value: 'Styling', label: 'Styling' },
                          { value: 'Business', label: 'Business' },
                          { value: 'Other', label: 'Other' },
                        ]}
                        value={(field.value || []).map((itm) => ({
                          label: itm,
                          value: itm,
                        }))}
                        onChange={(e) => {
                          const val = Array.isArray(e)
                            ? e.flatMap((itm) => itm.value)
                            : e?.value;
                          return field.onChange(val);
                        }}
                        styles={selectStyles}
                      />
                    )}
                  />
                </div>
                <div>
                  <Typography
                    as="label"
                    type="text-small"
                    style={{ marginBottom: 8 }}
                  >
                    <FormattedMessage defaultMessage="Date Range" />
                  </Typography>
                  <Controller
                    name="dateRange"
                    control={control}
                    render={({ field }) => {
                      const startDate = field.value?.start
                        ? new Date(field.value.start)
                        : null;
                      const endDate = field.value?.end
                        ? new Date(field.value.end)
                        : null;
                      return (
                        <DateRangeInput>
                          <DatePicker
                            selected={startDate}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(dates) => {
                              const [start, end] = dates;
                              if (start && end) {
                                field.onChange({
                                  start: start.getTime(),
                                  end: end.getTime(),
                                });
                              } else if (start) {
                                field.onChange({
                                  start: start.getTime(),
                                  end: null,
                                });
                              } else {
                                field.onChange(null);
                              }
                            }}
                            selectsRange
                            isClearable
                            placeholderText={intl.formatMessage({
                              defaultMessage: "Select date range",
                            })}
                            dateFormat="MM/dd/yyyy"
                          />
                        </DateRangeInput>
                      );
                    }}
                  />
                </div>
              </FieldsContainer>
            </Fields>
          </AdvancedContainer>
        </Container>
      )}
    </Root>
  );
};
